import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@material-ui/core";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import Countdown from "react-countdown";
import DefaultLayout from "../layouts/DefaultLayout";
import { OverviewContext } from "../store/OverviewContext";
import { PlayerContext } from "../store/PlayerContext";
import { Player } from "./common/player";
import Seo from "./common/Seo";
import TryButton from "./Onboarding/TryButton";
import { captureAttribution } from "../utils/attribution";
import { isSafeInternalPath } from "../utils/returnPath";
import { findLiveCampaign, OverviewWithCampaigns } from "../utils/liveCampaign";

// Server-driven campaign landing page (#596): /c/<slug>. EVERYTHING shown
// here — title, hero, body copy, offer, CTA, countdown target and the live
// window — comes from the overview `campaigns` record at runtime. Nothing is
// hardcoded (Campaign.no-literals.test.ts is the regression fence). The
// server only serves active, in-window campaigns, but the public overview
// rides a Redis cache, so the window is re-checked here at render time; a
// missing, inactive or out-of-window campaign quietly redirects Home.

const DESCRIPTION_LENGTH = 150;

// The preview-video path swallows load failures without an error signal
// (PlayerContext catch-and-log), so the hero spinner is time-boxed: past
// this, fall back to the image hero / plain title instead of spinning
// forever on a bad heroVideoSlug.
const HERO_VIDEO_TIMEOUT_MS = 8000;

const useStyles = makeStyles((theme) => ({
  background: {
    width: "100%",
    flexGrow: 1,
    // @ts-ignore
    backgroundColor: theme.palette.background.ffNavy,
  },
  loading: {
    textAlign: "center",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  heroImage: {
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    width: "100%",
    minHeight: "40vh",
    display: "flex",
    alignItems: "flex-end",
    boxShadow: "inset 0 -120px 90px -60px rgba(8, 5, 5, 0.9)",
    "@media (min-width:600px)": {
      minHeight: "50vh",
    },
  },
  title: {
    color: theme.palette.common.white,
    letterSpacing: "-2px",
    fontWeight: 600,
    fontSize: "2.4rem",
    lineHeight: "2.6rem",
    margin: theme.spacing(2, 0),
    textShadow: "-2px 2px 2px #171717",
    "@media (min-width:600px)": {
      fontSize: "3.6rem",
      lineHeight: "3.8rem",
    },
  },
  bodyText: {
    color: "#bbb",
    marginBottom: theme.spacing(2),
  },
  offerBox: {
    // @ts-ignore
    backgroundColor: theme.palette.background.ffNavyLighter,
    // @ts-ignore
    borderLeft: `4px solid ${theme.palette.background.ffGreen}`,
    borderRadius: "4px",
    padding: theme.spacing(2, 3),
    margin: theme.spacing(3, 0),
  },
  offerText: {
    // @ts-ignore
    color: theme.palette.background.ffGreen,
    fontWeight: 600,
  },
  countdown: {
    display: "flex",
    justifyContent: "center",
    margin: theme.spacing(3, 0),
  },
  countdownUnit: {
    textAlign: "center",
    // @ts-ignore
    backgroundColor: theme.palette.background.ffNavyLighter,
    borderRadius: "5px",
    padding: theme.spacing(1, 0),
    margin: theme.spacing(0, 0.5),
    width: "72px",
  },
  countdownValue: {
    color: theme.palette.common.white,
    fontSize: "1.8rem",
    fontWeight: 700,
    lineHeight: "2rem",
  },
  countdownLabel: {
    color: "#bbb",
    fontSize: "0.8rem",
  },
  ctaContainer: {
    textAlign: "center",
    margin: theme.spacing(3, 0),
  },
  ctaButton: {
    fontSize: "1.2rem",
    padding: theme.spacing(2, 6),
  },
  noUnderline: {
    textDecoration: "none",
  },
}));

const pad = (value: number): string => value.toString().padStart(2, "0");

const Campaign = () => {
  const classes = useStyles();

  const { slug } = useParams<{ slug: string }>();
  const { search } = useLocation();

  const { overview, loading, error } = useContext(OverviewContext);
  const { loadPreviewVideo, currentPreviewVideo, clearCurrentPreviewVideo } =
    useContext(PlayerContext);

  const [heroVideoTimedOut, setHeroVideoTimedOut] = useState(false);

  // Undefined unless the slug is live and renderable right now — the window
  // is re-checked at render time (see utils/liveCampaign.ts).
  const now = Date.now();
  const record = findLiveCampaign(overview, slug, now);

  const liveSlug = record ? record.slug : undefined;
  const heroVideoSlug = record ? record.heroVideoSlug : undefined;

  useEffect(() => {
    if (liveSlug) {
      captureAttribution(search, liveSlug);
    }
  }, [liveSlug, search]);

  useEffect(() => {
    // Drop the previous campaign's video first — an in-SPA move from
    // /c/a to /c/b reuses this component instance, and A's video must not
    // play in B's hero while B's loads.
    if (clearCurrentPreviewVideo) {
      clearCurrentPreviewVideo();
    }
    setHeroVideoTimedOut(false);

    if (!heroVideoSlug) {
      return;
    }

    loadPreviewVideo(heroVideoSlug);

    const timeout = setTimeout(() => {
      setHeroVideoTimedOut(true);
    }, HERO_VIDEO_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroVideoSlug]);

  useEffect(() => {
    return () => {
      if (clearCurrentPreviewVideo) {
        clearCurrentPreviewVideo();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Neutral state while the overview request is still in flight — we cannot
  // yet know whether this campaign exists.
  if (!overview) {
    // The fetch finished without an overview (rejected / errored): fail
    // closed to Home, exactly as if the campaigns key were absent. Never
    // an error page, never an infinite spinner.
    if (error && !loading) {
      return <Navigate to="/" replace />;
    }

    return (
      <DefaultLayout>
        <div
          data-testid="campaign-loading"
          className={`${classes.background} ${classes.loading}`}
        >
          <CircularProgress color="primary" />
        </div>
      </DefaultLayout>
    );
  }

  // Unknown slug, switched off, out of window, or no campaigns key at all
  // (the kill-switch): quietly go Home — never an error page.
  if (!record) {
    return <Navigate to="/" replace />;
  }

  const description = record.bodyCopy
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, DESCRIPTION_LENGTH);

  const paragraphs = record.bodyCopy
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);

  const renderCountdown = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      return null;
    }

    const units = [
      { label: "Days", value: days },
      { label: "Hours", value: hours },
      { label: "Mins", value: minutes },
      { label: "Secs", value: seconds },
    ];

    return (
      <div data-testid="campaign-countdown" className={classes.countdown}>
        {units.map((unit) => (
          <div key={unit.label} className={classes.countdownUnit}>
            <Typography className={classes.countdownValue}>
              {pad(unit.value)}
            </Typography>
            <Typography className={classes.countdownLabel}>
              {unit.label}
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  const renderCta = () => {
    const { label, href } = record.cta;

    // The href is server-supplied content: confine it to our own origin
    // (same guard as the login return path). Anything else: no CTA at all.
    if (!isSafeInternalPath(href)) {
      return null;
    }

    // A campaign can outlive the trial window it points at — when the CTA
    // targets onboarding while trials are closed, offer the existing
    // notify-me flow instead of a dead signup link. Normalised so
    // "/try/", "/Buy?x=1" and friends still match.
    const targetPath = href.split(/[?#]/)[0].replace(/\/+$/, "").toLowerCase();
    const targetsOnboarding = targetPath === "/try" || targetPath === "/buy";
    const overviewDoors = (overview as OverviewWithCampaigns).doors;

    if (targetsOnboarding && overviewDoors?.trialsOpen === false) {
      return (
        <TryButton
          label="Notify Me"
          modalType="notify"
          size="large"
          className={classes.ctaButton}
        />
      );
    }

    return (
      <Link to={href} className={classes.noUnderline}>
        <Button size="large" className={classes.ctaButton}>
          {label}
        </Button>
      </Link>
    );
  };

  return (
    <DefaultLayout>
      <div className={classes.background}>
        <Seo
          title={record.title}
          description={description}
          image={record.heroImageUrl}
          url={`https://farmflix.tv/c/${slug}`}
        />

        {record.heroVideoSlug &&
        !(heroVideoTimedOut && !currentPreviewVideo) ? (
          <Container>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h1" className={classes.title}>
                  {record.title}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {currentPreviewVideo ? (
                  <Player
                    userId={undefined}
                    video={currentPreviewVideo}
                    type="preview"
                  />
                ) : (
                  <div
                    data-testid="campaign-hero-loading"
                    className={classes.loading}
                  >
                    <CircularProgress color="primary" />
                  </div>
                )}
              </Grid>
            </Grid>
          </Container>
        ) : record.heroImageUrl ? (
          <div
            className={classes.heroImage}
            style={{ backgroundImage: `url(${record.heroImageUrl})` }}
          >
            <Container>
              <Typography variant="h1" className={classes.title}>
                {record.title}
              </Typography>
            </Container>
          </div>
        ) : (
          <Container>
            <Typography variant="h1" className={classes.title}>
              {record.title}
            </Typography>
          </Container>
        )}

        <Container maxWidth="md">
          {record.countdownTo && Date.parse(record.countdownTo) > now && (
            <Countdown
              date={Date.parse(record.countdownTo)}
              renderer={renderCountdown}
              zeroPadTime={2}
            />
          )}

          {paragraphs.map((paragraph, index) => (
            <Typography
              key={index}
              variant="body1"
              className={classes.bodyText}
            >
              {paragraph}
            </Typography>
          ))}

          {record.offerCopy && (
            <Box className={classes.offerBox}>
              <Typography variant="h6" className={classes.offerText}>
                {record.offerCopy}
              </Typography>
            </Box>
          )}

          <div className={classes.ctaContainer}>{renderCta()}</div>
        </Container>
      </div>
    </DefaultLayout>
  );
};

export default Campaign;
